import { Rive } from "@rive-app/canvas";

class RivePlayer extends HTMLElement {
  #ended = false;
  #rive;

  constructor () {
    super();

    this.attachShadow({ mode: "open" });
  }

  async connectedCallback() {
    const { stateMachines, src, autoplay, loading } = this;
    const lazy = (loading === "lazy");

    this.dispatchEvent(new Event("loadstart"));

    const canvas = document.createElement("canvas");
    this.shadowRoot.appendChild(canvas);
    canvas.style.width = "100%";
    canvas.style.height = "100%";

    const rive = this.#rive = await new Promise((resolve, reject) => {
      const player = this;
      const rive = new Rive({
        canvas,
        src,
        stateMachines,
        // Only autoplay if specified and not loading lazy.
        autoplay: lazy ? false : autoplay,
        onLoad() {
          player.dispatchEvent(new Event("canplay"));
          player.dispatchEvent(new Event("canplaythrough"));
          resolve(rive);
        },
        onLoadError(event) {
          reject(event);
        },
        onPlay() {
          player.dispatchEvent(new Event("play"));
        },
        onPause() {
          player.dispatchEvent(new Event("pause"));
        },
        onStop() {
          player.#ended = true;
          player.dispatchEvent(new Event("ended"));
        },
        onLoop() {

        },
        onStateChange() {

        },
        onAdvance() {

        },
      });
    });

    // If no state machine specified, use the first one.
    if (!stateMachines.length) {
      stateMachines.push(rive.stateMachineNames[0]);
      this.stateMachines = stateMachines;
      // Reset the instance to use new state machines.
      rive.reset({
        stateMachines,
      });
    }

    const width = Number(this.getAttribute("width"));
    const height = Number(this.getAttribute("height"));

    // Use dimensions from Rive if not set on the element
    if (!width || !height) {
      const { maxX, maxY } = rive.bounds;
      this.width = canvas.width = width || maxX;
      this.height = canvas.height = height || maxY;
    }

    rive.resizeDrawingSurfaceToCanvas();

    if (lazy) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            rive.play();
            observer.disconnect();
          }
        });
      }, {
        threshold: 1.0
      });

      observer.observe(this);
    }
  }

  //#region Media properties
  get src() { return this.getAttribute("src"); }
  get autoplay() { return this.hasAttribute("autoplay"); }
  get loading() { return this.getAttribute("loading"); }
  get ended() { return this.#ended; }
  get paused() { return this.#rive?.isPaused; }
  get playing() { return this.#rive?.isPlaying; }
  //#endregion Media properties

  //#region Custom properties
  get stateMachines() {
    return (this.getAttribute("state-matchine") || "")
      .split(/,\s+/).filter(Boolean);
  }

  set stateMachines(stateMachines) {
    this.setAttribute("state-machine", stateMachines.join(", "));
  }
  //#endregion Custom properties
}

customElements.define("rive-player", RivePlayer);
